import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropDown } from '@/components/design-system/molecules/dropdown';

interface Tenant {
  id: number;
  name: string;
  room_number: string;
}

interface TenantSelectionDropdownProps {
  selectedTenant: Tenant | null;
  setSelectedTenant: (tenant: Tenant | null) => void;
  tenants: Tenant[];
  disabled?: boolean;
}

const TenantSelectionDropdown: React.FC<TenantSelectionDropdownProps> = ({
  selectedTenant,
  setSelectedTenant,
  tenants,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTenants = useMemo(() => {
    if (!searchQuery) return tenants;

    const query = searchQuery.toLowerCase();
    return tenants.filter((tenant) => tenant.name.toLowerCase().includes(query));
  }, [tenants, searchQuery]);

  const placeholder =
    tenants.length === 0
      ? t('js.features.admin.transactions.create_payment_modal.no_tenant_found')
      : t('js.features.admin.transactions.create_payment_modal.tenant_placeholder');

  // Ensure value is always null or a Tenant object
  const dropdownValue = selectedTenant || null;

  return (
    <DropDown
      showLabel
      label={t('js.features.admin.transactions.create_payment_modal.tenant_label')}
      placeholder={placeholder}
      className="z-[90]"
      value={dropdownValue}
      onChange={setSelectedTenant}
      disabled={disabled || tenants.length === 0}
      {...(dropdownValue && {
        displayValue: `${dropdownValue.name} (Room #${dropdownValue.room_number})`,
      })}
      onTextInputChange={(event) => setSearchQuery(event.target.value)}
      required
    >
      <DropDown.DropDownOptions>
        {filteredTenants.map((tenant) => (
          <DropDown.DropDownOption key={tenant.id} value={tenant}>
            <DropDown.DropDownOptionDefaultContent>
              {tenant.name} (Room #{tenant.room_number})
            </DropDown.DropDownOptionDefaultContent>
          </DropDown.DropDownOption>
        ))}
      </DropDown.DropDownOptions>
    </DropDown>
  );
};

export { TenantSelectionDropdown };
